// src/pages/ListV2Page.tsx
import { useEffect, useMemo, useState } from "react";
import "./list.page.css";
import type { Task, TaskStatus, Priority, Project, Company } from "../types/domain";
import { useTasksQuery } from "../hooks/useTasksQuery";
import { updateTask, deleteTask } from "../services/tasks";
import { confirm } from "../lib/dialogs";
import { toast } from "react-hot-toast";
import GlobalHeader from "../components/layout/GlobalHeader";
import GlobalSidebar from "../components/layout/GlobalSidebar";
import TaskEditModal from "../components/modals/TaskEditModal";
import { supabase } from "../lib/supabase";

const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: "Backlog",
  todo: "Por hacer",
  in_progress: "En curso",
  in_review: "En revisión",
  done: "Hechas",
  archived: "Archivadas",
  deleted: "Eliminadas",
};
const PRIORITY_LABEL: Record<Priority, string> = {
  low: "Baja", medium: "Media", high: "Alta", urgent: "Urgente",
};
const PRIORITIES: (Priority | "")[] = ["", "low", "medium", "high", "urgent"];
const STATUSES: TaskStatus[] = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
  "archived",
];

export default function ListV2Page() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<TaskStatus | "">("");
  const [priority, setPriority] = useState<Priority | "">("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  const { tasks, loading, error, setFilters, reload } = useTasksQuery({
    search: "",
    orderBy: "id",
    ascending: false,
    limit: 1000,
  });

  useEffect(() => {
    const loadProjectsAndCompanies = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          const { data: allProjects } = await supabase
            .from("projects")
            .select("*")
            .order('name');

          if (allProjects) {
            setProjects(allProjects as Project[]);
          }

          const { data: allCompanies } = await supabase
            .from("companies")
            .select("*")
            .order('name');

          if (allCompanies) {
            setCompanies(allCompanies as Company[]);
          }
        }
      } catch (error) {
        console.error("Error cargando proyectos y empresas:", error);
      }
    };

    loadProjectsAndCompanies();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({
        ...f,
        search: search.trim() || undefined,
        status: (status as TaskStatus) || undefined,
      }));
    }, 200);
    return () => clearTimeout(t);
  }, [search, status, setFilters]);

  const filtered = useMemo(() => {
    let list = (tasks ?? []).slice();
    if (priority) list = list.filter((t) => t.priority === priority);
    if (onlyOverdue)
      list = list.filter((t) => {
        if (!t.deadline) return false;
        const today = new Date(); today.setHours(0,0,0,0);
        const d = new Date(t.deadline); d.setHours(0,0,0,0);
        return (
          d < today &&
          t.status !== "done" &&
          t.status !== "archived" &&
          t.status !== "deleted"
        );
      });
    return list;
  }, [tasks, priority, onlyOverdue]);

  const clearFilters = () => {
    setPriority("");
    setStatus("");
    setSearch("");
    setOnlyOverdue(false);
  };

  const handleProjectSelect = (newProjectId: string) => {
    setSelectedProjectId(newProjectId);
    if (newProjectId) {
      const project = projects.find(p => p.id === newProjectId);
      if (project && project.company_id) {
        setSelectedCompanyId(project.company_id);
      }
    }
  };

  return (
    <>
      <GlobalHeader
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        projects={projects}
        companies={companies}
        viewType="list"
        onRefreshClick={reload}
        onCreateTask={() => {
          setShowCreateTaskModal(true);
        }}
        onUserClick={() => {
          // User functionality
        }}
      />
      
      <GlobalSidebar
        selectedProjectId={selectedProjectId}
        selectedCompanyId={selectedCompanyId}
        priorityFilter={priority}
        assigneeFilter=""
        searchTerm={search}
        onProjectChange={handleProjectSelect}
        onCompanyChange={setSelectedCompanyId}
        onPriorityChange={setPriority}
        onAssigneeChange={() => {}}
        onSearchChange={setSearch}
        onClearFilters={clearFilters}
        projects={projects}
        companies={companies}
        tasks={tasks}
        showFilters={true}
        showProjectStats={true}
      />

      <div className="lst-wrap">
      <header className="lst-topbar">
        <h1 className="lst-title">Lista V2</h1>
        <div className="lst-controls">
          <input className="lst-input" placeholder="Buscar por título o descripción…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="lst-select" value={status} onChange={(e) => setStatus(e.target.value as any)}>
            <option value="">Todos los estados</option>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <select className="lst-select" value={priority} onChange={(e) => setPriority(e.target.value as any)}>
            <option value="">Todas las prioridades</option>
            {PRIORITIES.filter(Boolean).map((p) => <option key={p} value={p as string}>{PRIORITY_LABEL[p as any]}</option>)}
          </select>
          <label className="lst-check"><input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} /><span>Solo vencidas</span></label>
        </div>
      </header>

      {error && <div className="lst-error">⚠️ {error}</div>}
      {loading && <div className="lst-loading">Cargando…</div>}

      <div className="lst-table">
        <div className="lst-thead">
          <div>Título</div>
          <div>Descripción</div>
          <div>Estado</div>
          <div>Asignado</div>
          <div>Fecha</div>
          <div>Prioridad</div>
          <div>Acciones</div>
        </div>
        <div className="lst-tbody">
          {filtered.map((t) => (<Row key={t.id} task={t} onChange={reload} />))}
          {filtered.length === 0 && !loading && (<div className="lst-empty">No hay resultados con estos filtros.</div>)}
        </div>
      </div>

      <div className="lst-cards">
        {filtered.map((t) => (<Card key={t.id} task={t} onChange={reload} />))}
      </div>
      </div>

      {/* Modal de crear tarea */}
      <TaskEditModal
        task={null}
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onSave={async (newTask: Task) => {
          await reload();
          setShowCreateTaskModal(false);
        }}
        projects={projects}
        showStatusField={true}
        showDateField={true}
      />
    </>
  );
}

function Row({ task, onChange }: { task: Task; onChange: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [title, setTitle] = useState(task.title);
  const [desc, setDesc] = useState(task.description ?? "");
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const [assignee, setAssignee] = useState(task.assignee_id ?? "");
  const [priority, setPriority] = useState<Priority | "">(task.priority ?? "");
  const [deadline, setDeadline] = useState(toInputDate(task.deadline || ""));

  useEffect(() => {
    setTitle(task.title);
    setDesc(task.description ?? "");
    setStatus(task.status);
    setAssignee(task.assignee_id ?? "");
    setPriority(task.priority ?? "");
    setDeadline(toInputDate(task.deadline || ""));
  }, [task]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menu]);

  const save = async () => {
    const { error } = await updateTask(task.id, {
      title: title.trim() || task.title,
      description: desc.trim() || null,
      status,
      assignee_id: (assignee || null) as any,
      priority: (priority || null) as Priority | null,
      deadline: deadline || null,
    });
    if (error) toast.error("No se pudo guardar");
    else { toast.success("Guardado"); setEditing(false); }
    await onChange();
  };

  const archiveRestore = async () => {
    const willArchive = task.status !== "archived" && task.status !== "deleted";
    const ok = await confirm({
      title: willArchive ? "Archivar tarea" : "Restaurar tarea",
      text: willArchive ? "Se moverá a Archivadas." : "Volverá al tablero.",
      icon: "warning",
      confirmText: willArchive ? "Archivar" : "Restaurar",
    });
    if (!ok) return;
    const { error } = await updateTask(task.id, { status: willArchive ? "archived" : "todo" });
    if (error) toast.error("No se pudo actualizar");
    else toast.success(willArchive ? "Archivada" : "Restaurada");
    await onChange();
  };

  const remove = async () => {
    const ok = await confirm({ title: "Eliminar tarea", text: "No se puede deshacer.", icon: "warning", confirmText: "Eliminar" });
    if (!ok) return;
    const { error } = await deleteTask(task.id);
    if (error) toast.error("No se pudo eliminar");
    else toast.success("Eliminada");
    await onChange();
  };

  return (
    <div className="lst-tr" onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY }); }}>
      <div className="lst-td lst-title-text">{task.title}</div>
      <div className="lst-td"><span className="lst-desc">{task.description ?? "—"}</span></div>
      <div className="lst-td">{STATUS_LABEL[task.status]}</div>
      <div className="lst-td">{task.assignee_id || "—"}</div>
      <div className="lst-td">{task.deadline ? shortDate(task.deadline) : "—"}</div>
      <div className="lst-td">{task.priority ? PRIORITY_LABEL[task.priority] : "—"}</div>
      <div className="lst-td lst-actions">
        <button className="kb-card-action-btn" title="Editar" onClick={() => setEditing(true)}>✎</button>
        <button
          className="kb-card-action-btn"
          title={
            task.status === "archived" || task.status === "deleted"
              ? "Restaurar"
              : "Archivar"
          }
          onClick={() => void archiveRestore()}
        >
          {task.status === "archived" || task.status === "deleted" ? "↩" : "📦"}
        </button>
        <button className="kb-card-action-btn danger" title="Eliminar" onClick={() => void remove()}>🗑</button>
      </div>

      {editing && (
        <div className="lst-modal-overlay" onClick={() => setEditing(false)}>
          <div className="lst-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lst-modal-body">
              <input className="lst-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título" />
              <textarea className="lst-textarea" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Descripción" />
              <select className="lst-select" value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
                {Object.keys(STATUS_LABEL).map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s as TaskStatus]}</option>
                ))}
              </select>
              <input className="lst-input" value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="ID del usuario" />
              <input type="date" className="lst-input" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
              <select className="lst-select" value={priority} onChange={(e) => setPriority((e.target.value as any) || "")}> 
                <option value="">Sin prioridad</option>
                {PRIORITIES.filter(Boolean).map((p) => (
                  <option key={p} value={p as string}>{PRIORITY_LABEL[p as any]}</option>
                ))}
              </select>
            </div>
            <div className="lst-modal-actions">
              <button className="btn btn-primary" onClick={() => void save()}>Guardar</button>
              <button className="btn btn-ghost" onClick={() => setEditing(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {menu && (
        <div className="lst-menu" style={{ top: menu.y, left: menu.x }}>
          <button onClick={() => { setMenu(null); setEditing(true); }}>✎ Editar</button>
          <button onClick={() => { setMenu(null); void archiveRestore(); }}>
            {task.status === "archived" || task.status === "deleted" ? "↩ Restaurar" : "📦 Archivar"}
          </button>
          <button onClick={() => { setMenu(null); void remove(); }}>🗑 Eliminar</button>
        </div>
      )}
    </div>
  );
}

function Card({ task, onChange }: { task: Task; onChange: () => Promise<void> }) {
  return <div className="lst-card"><Row task={task} onChange={onChange} /></div>;
}

function toInputDate(v: string) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.length === 10 ? v : "";
  const y = d.getFullYear(); const m = `${d.getMonth() + 1}`.padStart(2, "0"); const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortDate(d: string) {
  try { const dt = new Date(d); return !Number.isNaN(dt.getTime()) ? dt.toLocaleDateString() : d; }
  catch { return d; }
}
